import React from 'react';
import { Stack } from '@atlaskit/primitives/compiled';
import { ErrorMessage, Field, HelperMessage } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { CheckboxSelect, type ValueType } from '@atlaskit/select';
import { useIntl } from '@atlassian/jira-intl';
import CopyButton from '../copy-button/index.tsx';
import type { SelectOption } from '../../../common/types.tsx';
import { validateSupportedScopes, validateURLField } from '../../../common/form-utils.tsx';
import { THIS_PRODUCT_NAME_STRING } from '../../../common/constants.tsx';
import messages from './messages.tsx';

export const CopyableApplicationClientID = (props: { name: string; clientId: string }) => {
	const { formatMessage } = useIntl();

	return (
		<Field
			aria-required
			name={props.name}
			defaultValue={props.clientId}
			label={formatMessage(messages.clientIdLabelNonfinal)}
			isDisabled
		>
			{({ fieldProps }) => (
				<Textfield
					isReadOnly
					type="password"
					{...fieldProps}
					elemAfterInput={
						<CopyButton
							label={formatMessage(messages.clientIdLabelNonfinal)}
							value={props.clientId}
						/>
					}
				/>
			)}
		</Field>
	);
};

export const CopyableApplicationClientSecret = (props: { name: string; clientSecret: string }) => {
	const { formatMessage } = useIntl();

	return (
		<Field
			aria-required
			name={props.name}
			defaultValue={props.clientSecret}
			label={formatMessage(messages.clientSecretLabelNonfinal)}
			isDisabled
		>
			{({ fieldProps }) => (
				<Textfield
					isReadOnly
					type="password"
					{...fieldProps}
					elemAfterInput={
						<CopyButton
							label={formatMessage(messages.clientSecretLabelNonfinal)}
							value={props.clientSecret}
						/>
					}
				/>
			)}
		</Field>
	);
};

export const RemoteApplicationScopeMultiSelect = (props: {
	name: string;
	supportedScopes: string[];
	defaultSelected?: ValueType<SelectOption, true>;
}) => {
	const { formatMessage } = useIntl();
	return (
		<Field<ValueType<SelectOption, true>>
			name={props.name}
			label={formatMessage(messages.remoteApplicationScopesLabelNonfinal)}
			defaultValue={props.defaultSelected}
			validate={(chosenScopes) =>
				validateSupportedScopes(
					chosenScopes ? chosenScopes.map((scope) => scope.value) : [],
					props.supportedScopes,
				)
			}
		>
			{({ fieldProps, error }) => (
				<Stack>
					<CheckboxSelect
						placeholder={formatMessage(messages.remoteApplicationScopesPlaceholderNonfinal)}
						{...fieldProps}
						options={props.supportedScopes.map((scope) => ({
							label: scope,
							value: scope,
						}))}
					/>
					{error && <ErrorMessage>{error}</ErrorMessage>}
				</Stack>
			)}
		</Field>
	);
};

export const ApplicationClientId = (props: { name: string; isRequired?: boolean }) => {
	const { formatMessage } = useIntl();

	return (
		<Field
			aria-required
			name={props.name}
			label={formatMessage(messages.clientIdLabelNonfinal)}
			isRequired={props.isRequired}
		>
			{({ fieldProps }) => (
				<Textfield
					type="password"
					placeholder={formatMessage(messages.clientIdLabelNonfinal)}
					{...fieldProps}
				/>
			)}
		</Field>
	);
};

export const ApplicationClientSecret = (props: { name: string; isRequired?: boolean }) => {
	const { formatMessage } = useIntl();

	return (
		<Field
			aria-required
			name={props.name}
			label={formatMessage(messages.clientSecretLabelNonfinal)}
			isRequired={props.isRequired}
		>
			{({ fieldProps }) => (
				<Textfield
					type="password"
					placeholder={formatMessage(messages.clientSecretLabelNonfinal)}
					{...fieldProps}
				/>
			)}
		</Field>
	);
};

export const RedirectUrl = (props: { name: string; defaultRedirectUrl?: string }) => {
	const { formatMessage } = useIntl();
	return (
		<Field
			aria-required
			name={props.name}
			defaultValue={props.defaultRedirectUrl}
			label={formatMessage(messages.redirectUrlLabelNonfinal)}
			validate={(redirectUrl) => validateURLField(redirectUrl, true, true)}
			isRequired
		>
			{({ fieldProps, error }) => (
				<>
					<Textfield
						placeholder={formatMessage(messages.redirectUrlLabelNonfinal)}
						{...fieldProps}
					/>
					{error && <ErrorMessage>{error}</ErrorMessage>}
				</>
			)}
		</Field>
	);
};

export const DisplayName = (props: { name: string; defaultName: string }) => {
	const { formatMessage } = useIntl();
	return (
		<Field
			aria-required
			name={props.name}
			defaultValue={props.defaultName}
			label={formatMessage(messages.displayNameLabelNonfinal)}
			isRequired
		>
			{({ fieldProps }) => (
				<Stack>
					<Textfield
						placeholder={formatMessage(messages.displayNamePlaceholderNonfinal)}
						{...fieldProps}
					/>
					<HelperMessage>{formatMessage(messages.displayNameHelperMessageNonfinal)}</HelperMessage>
				</Stack>
			)}
		</Field>
	);
};

export const DisplayUrl = (props: { name: string; defaultUrl: string }) => {
	const { formatMessage } = useIntl();
	return (
		<Field
			aria-required
			name={props.name}
			defaultValue={props.defaultUrl}
			label={formatMessage(messages.displayUrlLabelNonfinal)}
			isRequired
			validate={(defaultUrl) => validateURLField(defaultUrl, true, false)}
		>
			{({ fieldProps, error }) => (
				<Stack>
					<Textfield
						placeholder={formatMessage(messages.displayUrlPlaceholderNonfinal)}
						{...fieldProps}
					/>
					{!error && (
						<HelperMessage>
							{formatMessage(messages.displayUrlHelperMessageNonfinal, {
								productName: THIS_PRODUCT_NAME_STRING,
							})}
						</HelperMessage>
					)}
					{error && <ErrorMessage>{error}</ErrorMessage>}
				</Stack>
			)}
		</Field>
	);
};

export const ApplicationUrl = (props: { name: string; defaultUrl: string; disabled: boolean }) => {
	const { formatMessage } = useIntl();

	return (
		<Field
			aria-required
			name={props.name}
			defaultValue={props.defaultUrl}
			label={formatMessage(messages.applicationUrlLabelNonfinal)}
			isDisabled={props.disabled}
			isRequired={!props.disabled}
			validate={(redirectUrl) => validateURLField(redirectUrl, true, true)}
		>
			{({ fieldProps, error }) => (
				<Stack>
					<Textfield
						placeholder={formatMessage(messages.applicationUrlPlaceholderNonfinal)}
						{...fieldProps}
					/>
					{!error && (
						<HelperMessage>
							{formatMessage(messages.applicationUrlHelperMessageNonfinal, {
								productName: THIS_PRODUCT_NAME_STRING,
							})}
						</HelperMessage>
					)}
					{error && <ErrorMessage>{error}</ErrorMessage>}
				</Stack>
			)}
		</Field>
	);
};
